import React, { useEffect, useState } from 'react'
import { AddMaterialForm } from './AddMaterialForm'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { useToast } from '@/hooks/use-toast'
import { MaterialCard } from './MaterialCard'
import { EditMaterialDialog } from './EditMaterialDialog'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'

const allowedUnits = [
  { value: 'all', label: 'All Units' },
  { value: 'kg', label: 'Kilograms' },
  { value: 'grams', label: 'Grams' },
  { value: 'liters', label: 'Liters' },
  { value: 'ml', label: 'Milliliters' },
  { value: 'pieces', label: 'Pieces' },
]

interface Material {
  id: string;
  name: string;
  unit_type: string;
  current_stock: number;
  min_threshold: number;
  cost_per_unit: number;
  supplier?: string;
  created_at: string;
}

interface MaterialsListProps {
  selectedBranchId: string;
}

function MaterialsList({ selectedBranchId }: MaterialsListProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [materials, setMaterials] = useState<Material[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [unit, setUnit] = useState('all')
  const [refresh, setRefresh] = useState(0)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null)
  const [defaultTab, setDefaultTab] = useState<'edit' | 'restock' | 'adjust'>('edit')

  useEffect(() => {
    if (user) {
      loadMaterials();
    }
  }, [user, search, unit, refresh])

  const loadMaterials = async () => {
    if (!user || !selectedBranchId) return;
    
    setLoading(true);
    try {
      let query = (supabase as any)
        .from('raw_materials')
        .select('*')
        .eq('user_id', user.id)
        .eq('branch_id', selectedBranchId);

      if (search) {
        query = query.ilike('name', `%${search}%`);
      }

      if (unit && unit !== 'all') {
        query = query.eq('unit_type', unit);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setMaterials((data as Material[]) || []);
    } catch (error) {
      console.error('Error loading materials:', error);
      toast({
        title: 'Error loading materials',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this material?')) return;
    
    try {
      const { error } = await (supabase as any)
        .from('raw_materials')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) {
        throw error;
      }

      toast({ title: 'Material deleted successfully' });
      setRefresh(r => r + 1);
    } catch (error) {
      console.error('Error deleting material:', error);
      toast({
        title: 'Error deleting material',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
    }
  }

  const handleEdit = (material: Material) => {
    setSelectedMaterial(material);
    setDefaultTab('edit');
    setEditDialogOpen(true);
  }

  const handleMaterialUpdated = () => {
    setRefresh(r => r + 1);
    setEditDialogOpen(false);
    setSelectedMaterial(null);
  }

  const handleRestock = (material: Material) => {
    setSelectedMaterial(material);
    setDefaultTab('restock');
    setEditDialogOpen(true);
  }

  return (
    <div className="w-full">
      <div className="flex flex-col gap-2 md:gap-3 mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
          <Input
            placeholder="Search materials..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 text-sm md:text-base h-9 md:h-10"
            aria-label="Search materials"
          />
          <div className="flex gap-2">
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger className="w-full sm:w-40 text-sm md:text-base h-9 md:h-10">
                <SelectValue placeholder="Filter by unit" />
              </SelectTrigger>
              <SelectContent>
                {allowedUnits.map(u => (
                  <SelectItem key={u.value} value={u.value} className="text-sm">{u.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setRefresh(r => r + 1)} variant="outline" size="sm" className="h-9 md:h-10 px-3 text-sm">
              Refresh
            </Button>
          </div>
        </div>
      </div>
      <AddMaterialForm onSuccess={() => setRefresh(r => r + 1)} selectedBranchId={selectedBranchId} />
      <div className="mt-4 md:mt-6">
        {loading ? (
          <div className="text-center text-muted-foreground text-sm py-8">Loading...</div>
        ) : materials.length === 0 ? (
          <div className="text-center text-muted-foreground text-sm py-8">No materials found.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {materials.map(material => (
              <MaterialCard
                key={material.id}
                material={material}
                onDelete={() => handleDelete(material.id)}
                onEdit={() => handleEdit(material)}
                onRestock={() => handleRestock(material)}
              />
            ))}
          </div>
        )}
      </div>

      <EditMaterialDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        material={selectedMaterial}
        onMaterialUpdated={handleMaterialUpdated}
        defaultTab={defaultTab}
        selectedBranchId={selectedBranchId}
      />
    </div>
  )
}

export { MaterialsList } 